import { useMemo } from 'react';

import { useQuery } from '@tanstack/react-query';
import type { SetFilterParams } from 'ag-grid-community';
import { keyBy } from 'lodash';
import { CircleQuestionMarkIcon } from 'lucide-react';

import { useFeatureFlag } from '@/app/services/features';
import { CellRendererParams, ColDef } from '@/grid/types';
import { dateFormatter, utcDateTimeFormatter } from '@/grid/value-formatters';
import { dateOnlyFormatter } from '@/grid/value-formatters/dateOnlyFormatter';
import { useStringMap } from '@/hooks/useStringMap';
import { useAccessToken } from '@/services/auth';
import { shippingSitesOptions } from '@/services/shipping';
import type { ShipmentListItem } from '@/services/shipping/types';

import { BooleanToggleFilter } from '../components/boolean-toggle-filter';
import { METHOD_DISPLAY_VALUES } from './constants';
import { countRecaneGroups } from './countRecaneGroups';
import { ShippingMethod } from './enums';
import { RecaneEmbryo } from './types';

export const CONTENT_TYPE_FIELDS = [
  'hasEmbryos',
  'hasSemen',
  'hasCalves',
  'hasRecipients',
  'hasDonors',
];

export function useColumns(
  setAccountOverlayInfo: React.Dispatch<
    React.SetStateAction<ShipmentListItem['owner'] | undefined>
  >,
  preserveGridState = false,
) {
  const token = useAccessToken();
  const sitesRestResult = useQuery({
    ...shippingSitesOptions(token),
  });

  const sites = sitesRestResult.data;
  const siteMap = useMemo(() => keyBy(sites, (site) => site.id), [sites]);
  const siteIds = useMemo(() => sites?.map((site) => site.id) || [], [sites]);

  const shipmentStatusOptions = useStringMap('shipment_status');
  const statusKey = JSON.stringify(shipmentStatusOptions.options);
  const stableOptions = useMemo(
    () => shipmentStatusOptions.options,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [statusKey],
  );
  const effectiveOptions = preserveGridState
    ? stableOptions
    : shipmentStatusOptions.options;

  const shipmentStatusMap = useMemo(
    () => effectiveOptions && keyBy(effectiveOptions, (option) => option.value),
    [effectiveOptions],
  );
  const shipmentStatusValues = useMemo(
    () => effectiveOptions?.map((option) => option.value.toString()) || [],
    [effectiveOptions],
  );

  const showAmountColumn = useFeatureFlag('shipping_grid_amount_column');
  const isRecaneNormalizationEnabled = useFeatureFlag(
    'Shipping_recane_normalization',
  );

  return useMemo(
    () =>
      [
        {
          checkboxSelection: true,
          suppressMovable: true,
          lockPosition: true,
          headerName: 'Active',
          field: 'stateCode',
          initialWidth: 80,
          filter: 'agSetColumnFilter',
          filterParams: {
            values: [0, 1],
            valueFormatter: (params: { value: number }) =>
              params.value === 0 ? 'Active' : 'Inactive',
            suppressSelectAll: true,
          } satisfies Partial<SetFilterParams<any, number>>,
          valueFormatter: (params: { value: number }) => {
            return params.value === 0 ? 'Active' : 'Inactive';
          },
        },
        {
          headerName: 'Shipment Number',
          field: 'shipmentNumber',
          initialWidth: 90,
          filter: 'agTextColumnFilter',
        },
        {
          headerName: 'Contains Embryos',
          field: 'hasEmbryos',
          initialWidth: 90,
          cellDataType: 'boolean',
          filter: BooleanToggleFilter,
        },
        {
          headerName: 'Contains Semen',
          field: 'hasSemen',
          initialWidth: 90,
          cellDataType: 'boolean',
          filter: BooleanToggleFilter,
        },
        {
          headerName: 'Contains Calves',
          field: 'hasCalves',
          initialWidth: 90,
          cellDataType: 'boolean',
          filter: BooleanToggleFilter,
        },
        {
          headerName: 'Contains Recips',
          field: 'hasRecipients',
          initialWidth: 90,
          cellDataType: 'boolean',
          filter: BooleanToggleFilter,
        },
        {
          headerName: 'Contains Donors',
          field: 'hasDonors',
          initialWidth: 90,
          cellDataType: 'boolean',
          filter: BooleanToggleFilter,
        },
        {
          headerName: 'Recanes',
          field: 'recaneEmbryos',
          filter: false,
          sortable: false,
          valueGetter: (params) =>
            countRecaneGroups(
              (params.data?.recaneEmbryos as RecaneEmbryo[]) || [],
              isRecaneNormalizationEnabled,
            ),
          valueFormatter(params) {
            return params.value === 0 ? '' : params.value;
          },
          initialWidth: 75,
          suppressHeaderMenuButton: true,
        },
        {
          headerName: 'Need By Date',
          field: 'needByDate',
          filter: 'agDateColumnFilter',
          valueFormatter: dateOnlyFormatter,
          initialWidth: 100,
          sort: 'desc',
        },
        {
          headerName: 'Account',
          field: 'owner.code',
          filter: 'agTextColumnFilter',
          initialWidth: 90,
          cellRenderer({ data }: CellRendererParams<ShipmentListItem>) {
            const owner: string = data?.owner?.code || data?.owner?.name || '';
            if (!owner) {
              return '';
            }
            return (
              <>
                {owner}{' '}
                <button
                  className="flex h-full items-center rounded-full border-0 bg-transparent p-0"
                  type="button"
                  onClick={() => {
                    if (data?.owner) {
                      setAccountOverlayInfo(data.owner);
                    }
                  }}
                >
                  <CircleQuestionMarkIcon className="" />
                </button>
              </>
            );
          },
        },

        {
          headerName: 'Shipping Status',
          field: 'shipmentStatus',
          filter: 'agSetColumnFilter',
          initialWidth: 80,
          filterParams: {
            defaultToNothingSelected: true,
            values: shipmentStatusValues,
            suppressSorting: true,
            valueFormatter(params: { value: string }) {
              if (shipmentStatusMap) {
                return shipmentStatusMap[params.value]?.label;
              }
              return params.value;
            },
          } as SetFilterParams,
          valueFormatter(params: { value: string }) {
            if (shipmentStatusMap) {
              return shipmentStatusMap[params.value]?.label;
            }
            return params.value;
          },
        },
        {
          headerName: 'Shipping Method',
          field: 'shippingMethod',
          filter: 'agSetColumnFilter',
          filterParams: {
            values: Object.values(ShippingMethod).filter(
              (v) => typeof v === 'number' && v >= 100000000,
            ),
            valueFormatter: (params: { value: number }) => {
              return METHOD_DISPLAY_VALUES[params.value] || params.value;
            },
            defaultToNothingSelected: true,
          },
          valueFormatter: (params) => {
            return METHOD_DISPLAY_VALUES[params.value] || params.value;
          },
          initialWidth: 100,
        },
        {
          headerName: 'Ship From',
          field: 'shipFromSiteId',
          filter: 'agSetColumnFilter',
          filterParams: {
            defaultToNothingSelected: true,
            values: siteIds,
            suppressSorting: true,
            valueFormatter(params: { value: string }) {
              return siteMap[params.value]?.name;
            },
          } as SetFilterParams,
          valueFormatter(params) {
            return siteMap[params.value]?.name ?? '';
          },
          initialWidth: 100,
        },
        ...(showAmountColumn
          ? [
              {
                headerName: 'Amount',
                field: 'amount',
                filter: 'agNumberColumnFilter',
                initialWidth: 100,
                type: 'numericColumn',
              } as ColDef<ShipmentListItem>,
            ]
          : []),
        {
          headerName: 'Tank/Incubator',
          field: 'tank',
          filter: 'agTextColumnFilter',
          initialWidth: 100,
        },
        {
          headerName: 'Comment',
          field: 'comment',
          tooltipField: 'comment',
          filter: 'agTextColumnFilter',
          initialWidth: 200,
        },
        {
          headerName: 'Created By',
          field: 'createdBy.fullName',
          filter: 'agTextColumnFilter',
          initialWidth: 90,
        },
        {
          headerName: 'Created On',
          field: 'createdOn',
          filter: 'agDateColumnFilter',
          initialWidth: 160,
          valueFormatter: utcDateTimeFormatter,
        },

        {
          headerName: 'Ship Date',
          field: 'date',
          filter: 'agDateColumnFilter',
          initialWidth: 100,
          valueFormatter: dateFormatter,
        },
        {
          headerName: 'Ship Name',
          field: 'shipName',
          filter: 'agTextColumnFilter',
        },

        {
          headerName: 'Ship State',
          field: 'shipState',
          filter: 'agTextColumnFilter',
          initialWidth: 80,
        },
        {
          headerName: 'Tracking Number',
          field: 'trackingNumber',
          filter: 'agTextColumnFilter',
          initialWidth: 100,
        },
        {
          headerName: 'Packed By',
          field: 'packedBy.name',
          valueGetter: ({ data }) => data?.packedBy?.name,
          filter: 'agTextColumnFilter',
          initialWidth: 100,
        },
      ] satisfies ColDef<ShipmentListItem>[],
    [
      isRecaneNormalizationEnabled,
      setAccountOverlayInfo,
      shipmentStatusMap,
      shipmentStatusValues,
      siteIds,
      siteMap,
    ],
  );
}
